import { CaretLeft } from "@phosphor-icons/react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { NavSearchInput } from "~/shell/NavPrimitives";

export interface NavLayerHeaderProps {
  /** The layer's name. */
  title: string;
  /** Where the current route lives inside the layer, e.g. the section title. */
  subtitle?: string;
  icon?: ReactNode;
  /** Goes one layer up (the sections layer). */
  onBack: () => void;
  backLabel?: string;
  query: string;
  onQueryChange: (query: string) => void;
  searchPlaceholder?: string;
}

/**
 * Top of every sidebar layer (layered nav, 2026-09-09): the way back up, the layer's
 * title, and a search field that finds any destination in any section — so the left nav
 * reads as one level of a tree rather than a flat list.
 */
export function NavLayerHeader({
  title,
  subtitle,
  icon,
  onBack,
  backLabel,
  query,
  onQueryChange,
  searchPlaceholder,
}: NavLayerHeaderProps) {
  const { t } = useTranslation();

  return (
    <div className="flex shrink-0 flex-col gap-1 px-3 pt-2.5 pb-2">
      <button
        type="button"
        onClick={onBack}
        className="-ms-1 inline-flex h-6 items-center gap-1 self-start rounded-sm ps-1 pe-2 text-[12px] font-medium text-text-tertiary outline-none hover:bg-hover-bg hover:text-text focus-visible:bg-hover-bg focus-visible:text-text"
      >
        <CaretLeft size={12} aria-hidden className="rtl:rotate-180" />
        {backLabel ?? t("All sections")}
      </button>
      <div className="flex min-h-7 items-center gap-2 pb-1">
        {icon ? (
          <span className="inline-flex shrink-0 items-center text-text-secondary">{icon}</span>
        ) : null}
        <span className="truncate text-body font-semibold text-text">{title}</span>
        {subtitle ? (
          <span className="truncate text-small text-text-tertiary">· {subtitle}</span>
        ) : null}
      </div>
      <NavSearchInput
        compact
        value={query}
        onChange={onQueryChange}
        {...(searchPlaceholder ? { placeholder: searchPlaceholder } : {})}
      />
    </div>
  );
}
